/**
 * One liquidation toast per transaction (XS-8).
 *
 * Two independent paths announce a liquidation: the on-chain listener
 * (`useLiquidationEvents`) and the backend notification pipeline. Each claims
 * the transaction hash before toasting; a hash already claimed by the OTHER
 * source is refused. The same source may toast a hash more than once, because
 * one transaction can legitimately emit several distinct liquidation events
 * (collateral sold and deficit socialized, or started and completed).
 */

export type LiquidationToastSource = "rpc" | "notification";

export const LIQUIDATION_CLAIM_TTL_MS = 10 * 60 * 1000;
export const LIQUIDATION_CLAIM_LIMIT = 200;

const claims = new Map<string, { source: LiquidationToastSource; at: number }>();

const prune = (now: number) => {
  claims.forEach((claim, hash) => {
    if (now - claim.at > LIQUIDATION_CLAIM_TTL_MS) claims.delete(hash);
  });
};

/** True when `source` may toast this transaction. A missing hash cannot be deduped and is allowed. */
export const claimLiquidationToast = (
  txHash: unknown,
  source: LiquidationToastSource,
  now: number = Date.now()
): boolean => {
  if (typeof txHash !== "string" || !txHash.trim()) return true;
  const hash = txHash.trim().toLowerCase();
  prune(now);

  const existing = claims.get(hash);
  if (existing) return existing.source === source;

  claims.set(hash, { source, at: now });
  while (claims.size > LIQUIDATION_CLAIM_LIMIT) {
    claims.delete(claims.keys().next().value as string);
  }
  return true;
};

/** Test hook. */
export const resetLiquidationToastClaims = () => {
  claims.clear();
};
